import React, { useState, useMemo, useEffect } from "react";
import { MenuItem, RadialSelection } from "../types/index";
import { motion, AnimatePresence } from "framer-motion";
import CircleLogo from "./CircleLogo";

interface RadialMenuProps {
  kitchenId: string;
  kitchenName: string;
  kitchenLogo?: string;
  menuItems: { key: string; data: MenuItem }[];
  onItemSelect: (selection: RadialSelection) => void;
  onBack?: () => void;
}

type MenuNode = {
  id: string;
  name: string;
  price?: number;
  photoUrl?: string;
  children: MenuNode[];
  selectionData?: RadialSelection;
};

const buildMenuTree = (
  kitchenName: string,
  kitchenLogo: string | undefined,
  menuItems: { key: string; data: MenuItem }[]
): MenuNode => {
  const root: MenuNode = {
    id: "root",
    name: kitchenName,
    photoUrl: kitchenLogo,
    children: [],
  };

  menuItems.forEach(({ key, data: item }) => {
    const hasVariants = item.Variants && item.Variants.length > 0;
    const hasSizes = item.Sizes && item.Sizes.length > 0;
    
    const itemName = item.name || (item as any).Name || (item as any).Base || key;
    const basePrice = item.price ? Number(item.price) : 0;

    const itemNode: MenuNode = {
      id: key,
      name: itemName,
      photoUrl: item.photoUrl,
      price: item.price ?? 0, // ✅ Coerce undefined to 0
      children: [],
      selectionData: { item: { ...item, name: itemName, price: basePrice } },
    };

    const createSizeNodes = (parentSelection: RadialSelection): MenuNode[] => {
      if (!item.Sizes) return [];
      return item.Sizes.map((size) => ({
        id: `${parentSelection.variant ? parentSelection.variant + "_" : ""}${size.name}`,
        name: size.name,
        price: size.price ?? parentSelection.price ?? item.price ?? 0, // ✅ Cascade fallback
        photoUrl: size.photoUrl || item.photoUrl,
        children: [],
        selectionData: {
          ...parentSelection,
          size: size.name,
          price: size.price ?? parentSelection.price ?? item.price ?? 0, // ✅ Ensure price in selection
        },
      }));
    };

    if (hasVariants) {
      itemNode.children = item.Variants!.map((variant) => {
        const variantPrice = variant.price ?? item.price ?? 0; // ✅ Fallback to item price
        const variantSelection: RadialSelection = { 
          item: { ...item, name: itemName }, 
          variant: variant.name,
          price: variantPrice 
        };
        return {
          id: `${key}_var_${variant.name}`,
          name: variant.name,
          price: variantPrice, // ✅ Coerce to number
          photoUrl: variant.photoUrl || item.photoUrl,
          selectionData: variantSelection,
          children: hasSizes ? createSizeNodes(variantSelection) : [],
        };
      });
    } else if (hasSizes) {
      itemNode.children = createSizeNodes({ 
        item: { ...item, name: itemName, price: basePrice } 
      });
    }

    root.children.push(itemNode);
  });

  return root;
};

const RadialMenu: React.FC<RadialMenuProps> = ({
  kitchenId,
  kitchenName,
  kitchenLogo,
  menuItems,
  onItemSelect,
  onBack,
}) => {
  const menuTree = useMemo(
    () => buildMenuTree(kitchenName, kitchenLogo, menuItems),
    [kitchenName, kitchenLogo, menuItems]
  );

  const [activeNode, setActiveNode] = useState<MenuNode>(menuTree);
  const [history, setHistory] = useState<MenuNode[]>([]);

  useEffect(() => {
    setActiveNode(menuTree);
    setHistory([]);
  }, [menuTree]);

  const getLogoLayoutId = (nodeId: string) => {
    return `radial-logo-${kitchenId}-${nodeId}`;
  };

  const handleNodeClick = (node: MenuNode) => {
    if (node.children.length === 0) {
      if (node.selectionData) {
        onItemSelect(node.selectionData);
      }
      return;
    }
    setHistory((prev) => [...prev, activeNode]);
    setActiveNode(node);
  };

  const handleBackClick = () => {
    if (history.length === 0) {
      onBack?.();
      return;
    }
    const lastNode = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setActiveNode(lastNode);
  };

  const itemsToDisplay = activeNode.children;
  const totalItems = itemsToDisplay.length;
  const radius = 140;
  const isRoot = activeNode.id === "root";
  const historyNode = history.length > 0 ? history[history.length - 1] : null;

  let promptText = "Select an item";
  if (!isRoot) {
    if (history.length === 1) promptText = "Select Variant";
    else if (history.length === 2) promptText = "Select Cut";
  }

  // FIX: Explicitly cast AnimatePresence to any
  const AP = AnimatePresence as any;

  return (
    <div className="radial-menu-container relative w-full h-[400px] flex justify-center items-center overflow-hidden">
      {/* Static Outer Ring */}
      <div
        className="absolute rounded-full border-2 border-orange-200 opacity-30"
        style={{
          width: radius * 2,
          height: radius * 2,
          pointerEvents: "none",
          position: "absolute",
        }}
      />

      {/* History Node */}
      <AP>
        {historyNode && (
          <motion.div
            key="history-button"
            className="absolute cursor-pointer z-20 flex flex-col items-center"
            onClick={handleBackClick}
            initial={{ opacity: 0, x: -50, scale: 0.5 }}
            animate={{ x: -220, scale: 0.6, opacity: 0.6 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{ pointerEvents: "auto" }}
          >
            <CircleLogo
              src={historyNode.photoUrl || "/food-hero.svg"}
              alt={historyNode.name}
              size={100}
              borderColor="#e5e7eb"
              layoutId={getLogoLayoutId(historyNode.id)}
            />
            <div className="mt-2 bg-white/90 px-3 py-1 rounded-full shadow-sm text-xs font-bold text-gray-600 backdrop-blur-sm border border-gray-200">
              Back
            </div>
          </motion.div>
        )}
      </AP>

      {/* Active Center Node */}
      <motion.div
        key={activeNode.id}
        className="radial-center-item z-30 flex flex-col items-center justify-center cursor-pointer absolute"
        onClick={handleBackClick}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 350, damping: 25 }}
      >
        <CircleLogo
          src={activeNode.photoUrl || "/food-hero.svg"}
          alt={activeNode.name}
          size={120}
          borderColor="transparent"
          className="shadow-2xl"
          layoutId={getLogoLayoutId(activeNode.id)}
        />
        <div className="mt-4 text-center z-40">
          <motion.h3
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-bold text-gray-900 text-xl leading-tight px-3 py-1 bg-white/95 rounded-xl shadow-sm border border-gray-100 backdrop-blur-sm max-w-[180px]"
          >
            {activeNode.name}
          </motion.h3>

          {!isRoot && itemsToDisplay.length > 0 && (
            <span className="text-xs text-orange-700 font-bold block mt-1 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">
              {promptText}
            </span>
          )}
          {itemsToDisplay.length === 0 && isRoot && (
            <span className="text-sm text-gray-500 italic block mt-2 bg-white/80 px-2 rounded">
              No items available
            </span>
          )}
          {!isRoot && (
            <span className="text-xs text-orange-600 font-bold block mt-2 bg-white/90 px-3 py-1 rounded-full border border-orange-100 shadow-sm">
              Tap to go back
            </span>
          )}
        </div>
      </motion.div>

      {/* Ring Items */}
      <div className="absolute w-full h-full flex justify-center items-center pointer-events-none">
        <AP>
          {itemsToDisplay.map((node, i) => {
            const angle = (i / totalItems) * 2 * Math.PI - Math.PI / 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            return (
              <motion.div
                key={node.id}
                className="absolute flex flex-col items-center justify-center pointer-events-auto cursor-pointer group"
                style={{ x: 0, y: 0 }}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={{ x, y, opacity: 1, scale: 1 }}
                exit={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  delay: i * 0.03,
                }}
                onClick={() => handleNodeClick(node)}
              >
                <CircleLogo
                  src={node.photoUrl || "/food-hero.svg"}
                  alt={node.name}
                  size={65}
                  borderColor="#fdba74"
                  className="group-hover:border-orange-500 transition-colors shadow-md bg-white group-hover:scale-110 transform duration-200"
                  layoutId={getLogoLayoutId(node.id)}
                />
                <span className="mt-2 text-xs font-bold text-gray-800 bg-white/95 border border-gray-100 px-2 py-1 rounded shadow-sm max-w-[100px] truncate text-center group-hover:text-orange-700">
                  {node.name}
                </span>
                {node.price && !isNaN(node.price) && node.price > 0 ? (
                  <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100 -mt-1 z-10">
                    ₹{node.price.toFixed(0)}
                  </span>
                ) : null}
              </motion.div>
            );
          })}
        </AP>
      </div>
    </div>
  );
};

export default RadialMenu;
